import { useState } from "react";
import { Eye, Send, Undo2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import type { DraftChange } from "@/hooks/use-draft-edit";

interface Props {
  changes: DraftChange[];
  errors: { key: string; label: string; message: string }[];
  isDirty: boolean;
  canPublish: boolean;
  publishing: boolean;
  onPublish: () => void | Promise<void>;
  onDiscard: () => void;
  entityLabel?: string;
}

export function DraftPublishBar({
  changes,
  errors,
  isDirty,
  canPublish,
  publishing,
  onPublish,
  onDiscard,
  entityLabel = "changes",
}: Props) {
  const [previewOpen, setPreviewOpen] = useState(false);
  if (!isDirty) return null;

  const handlePublish = async () => {
    await onPublish();
    setPreviewOpen(false);
  };

  return (
    <>
      <div
        className="fixed left-2 right-2 z-50 rounded-2xl border border-border bg-background/95 px-3 py-3 shadow-pop backdrop-blur bottom-[calc(env(safe-area-inset-bottom,0px)+4.5rem)] md:bottom-4 md:left-1/2 md:right-auto md:w-[min(720px,calc(100vw-2rem))] md:-translate-x-1/2 md:rounded-3xl md:px-5"
        role="region"
        aria-label="Unpublished draft changes"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div
            className="flex items-center gap-2 text-sm min-w-0"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {errors.length > 0 ? (
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            )}
            <span className="font-semibold truncate">
              {changes.length} pending {entityLabel}
            </span>
            {errors.length > 0 && (
              <span
                className="text-xs text-amber-700 dark:text-amber-300 shrink-0"
                id="draft-bar-issues"
              >
                · {errors.length} {errors.length === 1 ? "issue" : "issues"}
              </span>
            )}
          </div>
          <div
            className="flex items-center justify-end gap-1.5 sm:gap-2"
            role="group"
            aria-label="Draft actions"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={onDiscard}
              disabled={publishing}
              className="rounded-full px-3 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={`Discard ${changes.length} pending ${entityLabel}`}
            >
              <Undo2 className="h-3.5 w-3.5 sm:mr-1" aria-hidden="true" />
              <span className="hidden sm:inline">Discard</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewOpen(true)}
              className="rounded-full px-3 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-haspopup="dialog"
            >
              <Eye className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Preview
            </Button>
            <Button
              size="sm"
              disabled={!canPublish}
              aria-disabled={!canPublish}
              aria-describedby={errors.length > 0 ? "draft-bar-issues" : undefined}
              onClick={handlePublish}
              className="rounded-full bg-gradient-hero text-primary-foreground px-3 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Send className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {publishing ? "Publishing…" : "Publish"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-hidden flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Preview {entityLabel}</DialogTitle>
            <DialogDescription>Review what will change before publishing.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-3">
            {errors.length > 0 && (
              <div
                role="alert"
                aria-live="assertive"
                className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200"
              >
                <div className="mb-1 flex items-center gap-2 font-semibold">
                  <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Resolve before publish
                </div>
                <ul className="ml-6 list-disc space-y-1">
                  {errors.map((e) => (
                    <li key={e.key}>
                      <span className="font-medium">{e.label}:</span> {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <ul
              className="divide-y divide-border rounded-2xl border border-border"
              aria-label={`${changes.length} pending ${entityLabel}`}
            >
              {changes.map((c) => (
                <li key={c.key} className="px-4 py-3 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {c.label}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="text-muted-foreground line-through break-words">
                      <span className="sr-only">From: </span>
                      {c.from}
                    </span>
                    <span aria-hidden="true">→</span>
                    <span className="font-medium break-words">
                      <span className="sr-only">To: </span>
                      {c.to}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row pb-[env(safe-area-inset-bottom)]">
            <Button
              variant="ghost"
              onClick={() => setPreviewOpen(false)}
              className="w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Keep editing
            </Button>
            <Button
              onClick={handlePublish}
              disabled={!canPublish}
              aria-disabled={!canPublish}
              className="rounded-full bg-gradient-hero text-primary-foreground w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Send className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {publishing ? "Publishing…" : "Publish now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
